import { Badge } from "@/components/ui/badge";
import {
  ExternalLink,
  Mail,
  ShieldX,
  ShieldAlert,
  ShieldCheck,
  Target,
  UserRound,
} from "lucide-react";
import { useTranslate } from "ra-core";

import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";

export const SalesPriorityBadge = ({
  priority,
}: {
  priority?: Company["lead_priority"];
}) => {
  const translate = useTranslate();
  if (!priority) return null;

  const className = {
    high: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
    medium:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
    low: "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
  }[priority];

  return (
    <Badge variant="outline" className={className}>
      {translate(`resources.companies.priority.${priority}`)}
    </Badge>
  );
};

export const MarketingConsentBadge = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const status = record.do_not_contact
    ? "stop_list"
    : record.marketing_consent_status || "unknown";
  const className = {
    granted:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
    requested:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300",
    unknown:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
    revoked:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
    stop_list:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
  }[status];
  const Icon = status === "granted" ? ShieldCheck : ShieldAlert;

  return (
    <Badge variant="outline" className={className}>
      <Icon />
      {translate(`resources.companies.consent_status.${status}`)}
    </Badge>
  );
};

export const SroMembershipBadge = ({
  status,
}: {
  status?: Company["sro_membership_status"];
}) => {
  const translate = useTranslate();
  if (!status) return null;
  const ceased = status === "ceased";
  const Icon = ceased ? ShieldX : ShieldCheck;

  return (
    <Badge
      variant="outline"
      className={
        ceased
          ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300"
          : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
      }
    >
      <Icon />
      {translate(`resources.companies.sro_membership.${status}`)}
    </Badge>
  );
};

export const CompanySpecializationBadges = ({
  record,
  limit,
}: {
  record: Company;
  limit?: number;
}) => {
  const { companySpecializations } = useConfigurationContext();
  const translate = useTranslate();
  const specializations = (record.specializations || [])
    .map(
      (value) =>
        companySpecializations.find((choice) => choice.value === value)
          ?.label || value,
    )
    .filter(Boolean);
  const visible = limit ? specializations.slice(0, limit) : specializations;
  if (!visible.length) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((label) => (
        <Badge
          key={label}
          variant="secondary"
          className="whitespace-normal text-left"
        >
          {label}
        </Badge>
      ))}
      {limit && specializations.length > limit ? (
        <Badge variant="outline">
          {translate("resources.companies.sales.more_specializations", {
            smart_count: specializations.length - limit,
          })}
        </Badge>
      ) : null}
    </div>
  );
};

export const CompanySalesIntelligence = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const hasResearch = Boolean(
    record.lead_priority ||
      record.lead_score != null ||
      record.decision_maker ||
      record.specializations?.length ||
      record.personalized_reason ||
      record.marketing_consent_status ||
      record.do_not_contact ||
      record.sro_membership_status,
  );
  if (!hasResearch) return null;

  const sourceUrls = record.research_source_urls || [];

  return (
    <AsideSection
      title={translate(
        "resources.companies.field_categories.sales_intelligence",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SalesPriorityBadge priority={record.lead_priority} />
        <SroMembershipBadge status={record.sro_membership_status} />
        <MarketingConsentBadge record={record} />
        {record.lead_score != null ? (
          <Badge variant="outline">
            <Target />
            {translate("resources.companies.sales.score", {
              score: record.lead_score,
            })}
          </Badge>
        ) : null}
      </div>

      {record.sro_membership_status === "ceased" ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
          <div className="font-medium">
            {translate("resources.companies.sales.sro_membership_ceased")}
          </div>
          {record.sro_membership_ended_at ? (
            <div>
              {translate("resources.companies.fields.sro_membership_ended_at")}:{" "}
              {record.sro_membership_ended_at}
            </div>
          ) : null}
          {record.sro_membership_end_reason ? (
            <div>{record.sro_membership_end_reason}</div>
          ) : null}
        </div>
      ) : null}

      {record.decision_maker ? (
        <div className="flex items-start gap-2">
          <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            {record.decision_maker_profile_url ? (
              <a
                href={record.decision_maker_profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline hover:no-underline"
              >
                {record.decision_maker}
              </a>
            ) : (
              <span className="font-medium">{record.decision_maker}</span>
            )}
            {record.decision_maker_role ? (
              <div className="text-muted-foreground">
                {record.decision_maker_role}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {record.research_email ? (
        <a
          href={`mailto:${record.research_email}`}
          className="flex min-w-0 items-center gap-2 underline hover:no-underline"
        >
          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{record.research_email}</span>
        </a>
      ) : null}

      {record.marketing_consent_channel ||
      record.marketing_consent_granted_at ||
      record.marketing_consent_evidence_url ? (
        <div className="text-xs text-muted-foreground">
          {record.marketing_consent_channel ? (
            <div>
              {translate(
                "resources.companies.fields.marketing_consent_channel",
              )}
              :{" "}
              {translate(
                `resources.companies.consent_channel.${record.marketing_consent_channel}`,
                { _: record.marketing_consent_channel },
              )}
            </div>
          ) : null}
          {record.marketing_consent_granted_at ? (
            <div>
              {translate(
                "resources.companies.fields.marketing_consent_granted_at",
              )}
              : {record.marketing_consent_granted_at}
            </div>
          ) : null}
          {record.marketing_consent_evidence_url ? (
            <a
              href={record.marketing_consent_evidence_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              {translate(
                "resources.companies.fields.marketing_consent_evidence_url",
              )}
            </a>
          ) : null}
        </div>
      ) : null}

      {(record.research_email || record.phone_number) &&
      (record.marketing_consent_status !== "granted" ||
        record.do_not_contact) ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          {translate("resources.companies.sales.public_contact_notice")}
        </div>
      ) : null}

      <CompanySpecializationBadges record={record} />

      <div className="flex flex-wrap gap-1">
        {record.digital_maturity_score != null ? (
          <Badge variant="outline">
            {translate("resources.companies.sales.digital_maturity", {
              score: record.digital_maturity_score,
            })}
          </Badge>
        ) : null}
        {(record.social_profile_count ?? 0) > 0 ? (
          <Badge variant="outline">
            {translate("resources.companies.sales.social_profiles", {
              smart_count: record.social_profile_count,
            })}
          </Badge>
        ) : null}
        {record.has_cases_clients ? (
          <Badge variant="outline">
            {translate("resources.companies.sales.has_cases_clients")}
          </Badge>
        ) : null}
        {record.has_vacancy_signal ? (
          <Badge variant="outline">
            {translate("resources.companies.sales.has_vacancy_signal")}
          </Badge>
        ) : null}
      </div>

      {record.personalized_reason ? (
        <div>
          <div className="font-medium">
            {translate("resources.companies.fields.personalized_reason")}
          </div>
          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
            {record.personalized_reason}
          </p>
        </div>
      ) : null}

      {record.research_checked_at ? (
        <div className="text-xs text-muted-foreground">
          {translate("resources.companies.sales.research_checked_at", {
            date: record.research_checked_at,
          })}
        </div>
      ) : null}

      {sourceUrls.length ? (
        <details className="group">
          <summary className="cursor-pointer font-medium">
            {translate("resources.companies.sales.sources", {
              smart_count: sourceUrls.length,
            })}
          </summary>
          <div className="mt-2 flex flex-col gap-1">
            {sourceUrls.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 items-center gap-1 underline hover:no-underline"
                title={url}
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{getSourceLabel(url)}</span>
              </a>
            ))}
          </div>
        </details>
      ) : null}
    </AsideSection>
  );
};

const getSourceLabel = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname.replace(/^www\./, "")}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  } catch {
    return url;
  }
};
